/* eslint-disable complexity */
/**
 * Reusable Order Confirmation Section Component
 * Used in both DetailsStep and RmcOrderEdit for consistent styling and behavior
 */

import {useConfirmationPin} from '@hconnect/tuc-processes/fields/confirmationPin/useConfirmationPin'
import {useIsConfirmed} from '@hconnect/tuc-processes/fields/isConfirmed/useIsConfirmed'
import {Box, Typography} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {RadioButtonGroup} from '../../../../../../../Molecules/RadioButton'

import {ConfirmationBanner} from './ConfirmationBanner'
import type {ConfirmationBannerProps} from './ConfirmationBanner'
import {ConfirmationVariationCodeBanner} from './ConfirmationVariationCodeBanner'

/**
 * Minimum order fields required to render the variation code banner.
 * Both Order (Pages) and OrderDto (tuc-processes) satisfy this shape.
 */
interface VariationCodeOrder {
  variationCode?: string
  variationCodeOffset?: string
}

/**
 * Props for the Order Confirmation Section component
 */
interface OrderConfirmationSectionProps {
  title?: string
  onConfirmationChange?: (confirmed: boolean) => void
  /**
   * Whether the confirmation section is disabled
   * When disabled, the buttons are not interactive
   */
  disabled?: boolean
  /**
   * Message to display when the section is disabled
   */
  disabledMessage?: string
  /**
   * Whether PIN validation is required before confirming
   * When true and PIN is not validated, shows PIN validation banner
   */
  isPinRequired?: boolean
  /**
   * Order object to derive variation code banner content.
   * When provided and the user has selected yes/no, the variation code banner
   * will be displayed for applicable variation codes (UE, CC).
   */
  order?: VariationCodeOrder
}

/**
 * Order Confirmation Section component with consistent styling
 */
export const OrderConfirmationSection: React.FC<OrderConfirmationSectionProps> = ({
  title,
  onConfirmationChange,
  disabled = false,
  disabledMessage,
  isPinRequired = false,
  order
}) => {
  const {t} = useTranslation()
  const {isConfirmed, setIsConfirmed, confirmationPinRequired} = useIsConfirmed()
  const {confirmationPin} = useConfirmationPin()

  // Determine if PIN banner should be shown
  // Show when: PIN is required (from config or prop), PIN not yet validated, and order is not confirmed
  const showPinBanner =
    (isPinRequired || confirmationPinRequired) && !confirmationPin && !isConfirmed

  // Compute banner mode based on state priority
  const bannerProps: ConfirmationBannerProps = useMemo(() => {
    // Priority 1: Disabled with message
    if (disabled && disabledMessage) {
      return {mode: 'disabled', message: disabledMessage}
    }
    // Priority 2: PIN validation required
    if (showPinBanner) {
      return {mode: 'pin-required'}
    }
    // Priority 3: Warning when not confirmed
    if (!isConfirmed) {
      return {mode: 'warning'}
    }
    // No banner needed
    return {mode: 'none'}
  }, [disabled, disabledMessage, showPinBanner, isConfirmed])

  const defaultTitle = t('orderIntake.rmc.steps.shared.orderConfirmation.title')
  const displayTitle = title || defaultTitle

  const variationCode = order?.variationCode ?? ''
  const variationCodeOffset = order?.variationCodeOffset ?? ''

  const showVariationCodeBanner =
    isConfirmed !== undefined && variationCode && variationCodeOffset && !disabled

  const handleConfirmationChange = (confirmed: boolean) => {
    if (disabled) return
    void setIsConfirmed(confirmed)
    onConfirmationChange?.(confirmed)
  }

  return (
    <Box data-test-id="order-confirmation-section">
      <Typography
        sx={{
          fontFamily: 'Inter',
          fontWeight: 600,
          fontSize: '18px',
          lineHeight: '24px',
          color: '#29333D',
          marginBottom: '20px'
        }}
      >
        {displayTitle}
      </Typography>

      {/* Radio Button Selection */}
      <RadioButtonGroup
        value={isConfirmed ?? false}
        onValueChange={handleConfirmationChange}
        options={[
          {
            label: t('orderIntake.rmc.steps.shared.orderConfirmation.yes'),
            value: true,
            variant: 'neutral',
            testId: 'order-confirmation-toggle-yes'
          },
          {
            label: t('orderIntake.rmc.steps.shared.orderConfirmation.no'),
            value: false,
            variant: 'warning',
            testId: 'order-confirmation-toggle-no'
          }
        ]}
        direction="row"
        spacing={2}
        disabled={disabled}
        testId="order-confirmation-radio-group"
      />

      {/* Contextual Banner */}
      <ConfirmationBanner {...bannerProps} />

      {/* Variation Code Banner — shown after user selects yes or no */}
      {showVariationCodeBanner && (
        <ConfirmationVariationCodeBanner
          variationCode={variationCode}
          variationCodeOffset={variationCodeOffset}
        />
      )}
    </Box>
  )
}
